/**
 * "Reload history" (SPEC-ADDENDA A1.4, ADR-0004, ADR-0028): the one entry point every surface
 * uses — the chart header's history menu (`HistoryMenu`), the chart right-click menu and the
 * command palette (`history.reload`, `history.reload.visible`, `history.reload.symbol`).
 *
 * Scopes:
 * - `visible` — the cached days (sessions, for tick / volume / range / Renko charts) under the
 *   chart's visible bars; the rest of the chart stays;
 * - `spec` — this symbol + timeframe, every cached day (the chart is wiped and refetched);
 * - `symbol` — every cached timeframe of the symbol (and every chart showing it).
 *
 * Each evicts the chosen cache entries and refetches them through the normal paged fetcher (one
 * `force_reload` request; the gateway pages it into replay windows). One reload per scope +
 * symbol + timeframe per 10 s ⚙ (`HISTORY_RELOAD_MIN_INTERVAL_MS`, the gateway's own rule for
 * forced requests); a refused one says when it is allowed again. Progress shows as the chart's
 * history chip ("Reloading 3/12 windows").
 */
import type { ChartHandle } from "./renderHost";
import { renderHost } from "./renderHost";
import type { ReloadScope } from "./protocol";

export type { ReloadScope };

export type HistoryReloadItem = { scope: ReloadScope; label: string; hint: string };

/** Menu entries, in menu order (header menu, right-click menu, palette titles). */
export const HISTORY_RELOAD_ITEMS: readonly HistoryReloadItem[] = [
  {
    scope: "visible",
    label: "Reload history (visible range)",
    hint: "Evict the cached days under the visible bars and refetch them",
  },
  {
    scope: "spec",
    label: "Reload history (this symbol + timeframe)",
    hint: "Evict every cached day of this symbol + timeframe and refetch",
  },
  {
    scope: "symbol",
    label: "Reload history (all timeframes of this symbol)",
    hint: "Evict every cached timeframe of this symbol and refetch the charts showing it",
  },
];

/**
 * Reload one chart's history. For the chart right-click menu (pass the chart's `ChartHandle`,
 * e.g. from `ChartWidget`'s handle) and the header menu. Returns false when there is no chart.
 * The outcome (progress, refusal, "History reloaded (N bars)") appears on that chart.
 */
export function reloadChartHistory(handle: ChartHandle | null, scope: ReloadScope): boolean {
  if (!handle) return false;
  handle.reloadHistory(scope);
  return true;
}

/** Palette: reload the most recently focused chart (every chart when none was focused). */
export function reloadFocusedChartHistory(scope: ReloadScope): void {
  renderHost().reloadHistory(scope);
}
